import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Zap,
  ArrowLeft,
  MapPin,
  Briefcase,
  Calendar,
  Mail,
  MessageSquare,
  Award,
  Building2,
  GraduationCap,
  Target,
  Star,
  ExternalLink,
  Video,
  Loader2,
  AlertCircle,
  Lock,
  Unlock
} from "lucide-react";

const CandidateProfile = () => {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [candidate, setCandidate] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startingVideoCall, setStartingVideoCall] = useState(false);
  const [contactUnlocked, setContactUnlocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [contactCredits, setContactCredits] = useState(0);

  useEffect(() => {
    if (candidateId) {
      fetchCandidate();
      if (user?.role === "recruiter") {
        checkContactUnlocked();
        fetchContactCredits();
      }
    }
  }, [candidateId]);

  const checkContactUnlocked = async () => {
    try {
      const res = await fetch(`${API}/credits/contacts/check/${candidateId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setContactUnlocked(data.unlocked);
      }
    } catch {}
  };

  const fetchContactCredits = async () => {
    try {
      const res = await fetch(`${API}/credits/contacts`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setContactCredits(data.credits);
      }
    } catch {}
  };

  const handleUnlockContact = async () => {
    setUnlocking(true);
    try {
      const res = await fetch(`${API}/credits/contacts/unlock/${candidateId}`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setContactUnlocked(true);
        setContactCredits(data.credits_remaining);
        toast.success("Contact info unlocked!");
      } else {
        const err = await res.json().catch(() => ({}));
        if (res.status === 402) {
          toast.error("No contact credits left. Purchase more in Add-ons.");
        } else {
          toast.error(err.detail || "Failed to unlock");
        }
      }
    } catch {
      toast.error("Failed to unlock contact");
    } finally {
      setUnlocking(false);
    }
  };

  const fetchCandidate = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API}/candidates/${candidateId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCandidate(data.user);
        setProfile(data.profile);
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.detail || "Candidate not found");
        toast.error(errData.detail || "Candidate not found");
      }
    } catch (err) {
      console.error("Failed to fetch candidate:", err);
      setError("Failed to load candidate profile");
      toast.error("Failed to load candidate profile");
    } finally {
      setLoading(false);
    }
  };

  const handleMessage = () => {
    navigate(`/messages?to=${candidateId}&name=${encodeURIComponent(candidate?.name || "Candidate")}`);
  };

  const handleInviteInterview = async () => {
    try {
      const response = await fetch(`${API}/ai/interview/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          candidate_id: candidateId,
          num_questions: 6,
          time_limit_minutes: 30
        }),
      });

      if (response.ok) {
        toast.success(`Interview invitation sent to ${candidate?.name}!`);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to send invitation");
      }
    } catch (err) {
      toast.error("Failed to send interview invitation");
    }
  };

  const handleStartVideoCall = async () => {
    setStartingVideoCall(true);
    try {
      // Create a video call room for direct interview
      const videoCallId = `direct-${candidateId}-${Date.now()}`;
      const res = await fetch(`${API}/video-calls/create-room`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          interview_id: videoCallId,
          candidate_id: candidateId,
          duration_minutes: 60
        }),
      });

      if (res.ok) {
        toast.success("Video call room ready!");
        navigate(`/video-call/${videoCallId}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create video call");
      }
    } catch (err) {
      toast.error("Failed to start video call");
    } finally {
      setStartingVideoCall(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex">
        <Sidebar />
        <main className="flex-1 lg:ml-64 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-crimson animate-spin" />
        </main>
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <div className="min-h-screen bg-obsidian flex">
        <Sidebar />
        <main className="flex-1 lg:ml-64 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Candidate Not Found</h2>
            <p className="text-gray-400 mb-6">{error || "Unable to load candidate profile"}</p>
            <Button onClick={() => navigate(-1)} className="bg-crimson hover:bg-crimson-hover">
              Go Back
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian flex">
      <Sidebar />

      <main className="flex-1 lg:ml-64 p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          {/* Profile Header */}
          <motion.div
            className="bg-obsidian-light border border-[#222] p-8 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-start gap-6">
              <div className="w-24 h-24 bg-crimson/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {candidate.picture ? (
                  <img src={candidate.picture} alt={candidate.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-4xl font-bold text-crimson">
                    {candidate.name?.charAt(0) || "?"}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <h1 className="font-heading text-3xl font-bold mb-2">{candidate.name}</h1>
                {profile?.headline && (
                  <p className="text-xl text-gray-400 mb-3">{profile.headline}</p>
                )}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  {profile?.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {profile.location}
                    </span>
                  )}
                  {profile?.career_level && (
                    <span className="flex items-center gap-1">
                      <Target className="w-4 h-4" />
                      {profile.career_level}
                    </span>
                  )}
                  {candidate.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {user?.role === "recruiter" && !contactUnlocked ? (
                        <span className="flex items-center gap-2">
                          <span className="blur-sm select-none">{candidate.email}</span>
                          <button
                            onClick={handleUnlockContact}
                            disabled={unlocking}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-crimson/20 border border-crimson/40 text-crimson text-xs hover:bg-crimson/30 transition-colors"
                            data-testid="unlock-contact-btn"
                          >
                            {unlocking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lock className="w-3 h-3" />}
                            Unlock ({contactCredits} credits)
                          </button>
                        </span>
                      ) : (
                        candidate.email
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Summary */}
              {profile?.summary && (
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <h3 className="font-heading text-lg font-bold mb-4">ABOUT</h3>
                  <p className="text-gray-300 whitespace-pre-wrap">{profile.summary}</p>
                </motion.div>
              )}

              {/* Experience */}
              {profile?.experience?.length > 0 && (
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <h3 className="font-heading text-lg font-bold mb-4">EXPERIENCE</h3>
                  <div className="space-y-6">
                    {profile.experience.map((exp, index) => (
                      <div key={index} className="relative pl-6 border-l-2 border-crimson/30">
                        <div className="absolute -left-[9px] top-0 w-4 h-4 bg-crimson rounded-full" />
                        <h4 className="font-bold text-lg">{exp.title}</h4>
                        <p className="text-crimson">{exp.company}</p>
                        <p className="text-gray-500 text-sm mb-2">
                          {exp.start_date} - {exp.end_date || "Present"}
                          {exp.location && ` • ${exp.location}`}
                        </p>
                        {exp.description && (
                          <p className="text-gray-400 text-sm">{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Education */}
              {profile?.education?.length > 0 && (
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h3 className="font-heading text-lg font-bold mb-4">EDUCATION</h3>
                  <div className="space-y-4">
                    {profile.education.map((edu, index) => (
                      <div key={index} className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-crimson/10 flex items-center justify-center flex-shrink-0">
                          <GraduationCap className="w-5 h-5 text-crimson" />
                        </div>
                        <div>
                          <h4 className="font-bold">{edu.degree}</h4>
                          <p className="text-gray-400">{edu.school}</p>
                          <p className="text-gray-500 text-sm">{edu.year}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Skills */}
              {profile?.skills?.length > 0 && (
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-6"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <h3 className="font-heading text-lg font-bold mb-4">SKILLS</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-crimson/10 border border-crimson/30 text-crimson text-sm"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Industries */}
              {profile?.industries?.length > 0 && (
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-6"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <h3 className="font-heading text-lg font-bold mb-4">TARGET INDUSTRIES</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.industries.map((industry, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-[#1a1a1a] text-gray-400 text-sm"
                      >
                        {industry}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Preferences */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h3 className="font-heading text-lg font-bold mb-4">PREFERENCES</h3>
                <div className="space-y-3 text-sm">
                  {profile?.remote_preference && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Remote Work</span>
                      <span className="text-white capitalize">{profile.remote_preference.replace("_", " ")}</span>
                    </div>
                  )}
                  {profile?.availability && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Availability</span>
                      <span className="text-white capitalize">{profile.availability}</span>
                    </div>
                  )}
                  {profile?.salary_expectation && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Expected Salary</span>
                      <span className="text-crimson">${profile.salary_expectation.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Actions */}
              {user?.role === "recruiter" && (
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-6"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h3 className="font-heading text-lg font-bold mb-4">ACTIONS</h3>
                  <div className="space-y-3">
                    <Button
                      onClick={handleMessage}
                      className="w-full bg-crimson hover:bg-crimson-hover text-white"
                      data-testid="send-message-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" /> Send Message
                    </Button>
                    <Button
                      onClick={handleStartVideoCall}
                      disabled={startingVideoCall}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="start-video-call-btn"
                    >
                      {startingVideoCall ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Video className="w-4 h-4 mr-2" />
                      )}
                      Start Video Call
                    </Button>
                    <Button
                      onClick={handleInviteInterview}
                      variant="outline"
                      className="w-full border-crimson text-crimson hover:bg-crimson hover:text-white"
                      data-testid="invite-interview-btn"
                    >
                      <Video className="w-4 h-4 mr-2" /> Send AI Interview
                    </Button>
                    <Button
                      onClick={() => navigate(`/candidates/compare?ids=${candidateId}`)}
                      variant="outline"
                      className="w-full border-[#333] hover:border-crimson text-white"
                      data-testid="add-comparison-btn"
                    >
                      <Award className="w-4 h-4 mr-2" /> Add to Comparison
                    </Button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CandidateProfile;
